import { Stack } from '@mui/material';
import type { ReactNode } from 'react';

import ArrowBack from '@/assets/svg/ui-elements/arrow-back.svg';
import IdCardIcon from '@/assets/svg/ui-elements/id-card.svg';
import ImportIcon from '@/assets/svg/ui-elements/import.svg';
import MailIcon from '@/assets/svg/ui-elements/mail.svg';
import QrCodeIcon from '@/assets/svg/ui-elements/qr-code.svg';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { Badge, IconButton, theme, Tooltip, Typography } from '@/shared/design-system';
import { DeniedReasonTrigger } from '@/shared/modules/permissions';

import { DisabledMethodCard, MethodCard, MethodIconBox, MethodTextBlock } from './styled';

export const ADD_CONTACT_BLOCKED_TOOLTIP = 'You do not have permission to add a contact';
export const IMPORT_BLOCKED_TOOLTIP = 'You do not have permission to import contacts';

interface MethodOptionProps {
  icon: ReactNode;
  title: string;
  badge?: ReactNode;
  description: string;
  onPick: () => void;
  enabled: boolean;
  blockedReason: string;
}

const MethodOption = ({
  icon,
  title,
  badge,
  description,
  onPick,
  enabled,
  blockedReason,
}: MethodOptionProps) => {
  const Card = enabled ? MethodCard : DisabledMethodCard;

  return (
    <Tooltip title={enabled ? '' : blockedReason}>
      <DeniedReasonTrigger denied={!enabled} style={{ width: '100%' }}>
        <Card onClick={enabled ? onPick : undefined} aria-disabled={!enabled}>
          <MethodIconBox>{icon}</MethodIconBox>
          <MethodTextBlock>
            <Stack alignItems="center" gap="6px">
              <Typography variant="body1" fontWeight={600}>
                {title}
              </Typography>
              {badge}
            </Stack>
            <Typography variant="body3" color={theme.colors.white[60]}>
              {description}
            </Typography>
          </MethodTextBlock>
          <IconButton
            color="tertiary"
            variant="outline"
            size="sm"
            disabled={!enabled}
            aria-label={title}
          >
            <ArrowBack transform="rotate(180)" />
          </IconButton>
        </Card>
      </DeniedReasonTrigger>
    </Tooltip>
  );
};

interface MethodSelectStepProps {
  onPickQr: () => void;
  onPickCard?: () => void;
  onPickManual: () => void;
  onPickImport?: () => void;
  canImport?: boolean;
  canCreate?: boolean;
}

export const MethodSelectStep = ({
  onPickQr,
  onPickCard,
  onPickManual,
  onPickImport,
  canImport = true,
  canCreate = true,
}: MethodSelectStepProps) => {
  const isMobile = useIsMobile();

  return (
    <Stack flexDirection="column" gap="32px">
      <Stack flexDirection="column" gap="8px">
        <MethodOption
          icon={<QrCodeIcon />}
          title="Scan QR Code"
          badge={!isMobile && <Badge label="Suggested" color="primary" variant="subtle" />}
          description="Instant connect via QR"
          onPick={onPickQr}
          enabled={canCreate}
          blockedReason={ADD_CONTACT_BLOCKED_TOOLTIP}
        />

        {onPickCard && (
          <MethodOption
            icon={<IdCardIcon />}
            title="Upload Business Card"
            description="Snap a card — we'll fill the rest"
            onPick={onPickCard}
            enabled={canCreate}
            blockedReason={ADD_CONTACT_BLOCKED_TOOLTIP}
          />
        )}

        <MethodOption
          icon={<MailIcon />}
          title="Manual Entry"
          description="Type in their details yourself"
          onPick={onPickManual}
          enabled={canCreate}
          blockedReason={ADD_CONTACT_BLOCKED_TOOLTIP}
        />

        {onPickImport && (
          <MethodOption
            icon={<ImportIcon />}
            title="Import"
            description="From Gmail, Office 365 or CSV file"
            onPick={onPickImport}
            enabled={canImport}
            blockedReason={IMPORT_BLOCKED_TOOLTIP}
          />
        )}
      </Stack>
    </Stack>
  );
};
